import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { alpha } from '@mui/material/styles';
import { getGuestDrawerUploadPaperSx } from '../../utils/bookingDrawerLayout';
import { captureNativeCameraDataUrl, getNativeCameraErrorMessage } from '../../utils/nativeCameraCapture';

export default function GuestDocumentSideEditor({
  label = 'Front',
  imageUrl = '',
  isDark = false,
  accent = '#4ce1b6',
  cameraUpload = false,
  isEditing = false,
  disabled = false,
  saving = false,
  onUpload,
  onRemove,
}) {
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const [permissionSnackbar, setPermissionSnackbar] = useState({ open: false, message: '' });

  const showCameraPermissionMessage = (message) => {
    setPermissionSnackbar({
      open: true,
      message:
        message ||
        'Camera access was denied or is unavailable. Allow camera permission in your browser settings for this site, or turn off Camera upload to choose a file.',
    });
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setCameraOpen(false);
  };

  useEffect(() => () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  }, [stream]);

  const commitUpload = async (dataUrl) => {
    if (!dataUrl || !onUpload) return;
    await onUpload(dataUrl);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await commitUpload(reader.result);
      } catch {
        // Parent handles errors
      }
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    try {
      const nativeImageData = await captureNativeCameraDataUrl();
      if (nativeImageData) {
        await commitUpload(nativeImageData);
        return;
      }
    } catch (error) {
      const message = getNativeCameraErrorMessage(error);
      if (message) {
        showCameraPermissionMessage(message);
        return;
      }
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      showCameraPermissionMessage(
        'Camera is not available in this browser or page context. Turn off Camera upload to upload a file instead.'
      );
      return;
    }

    try {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      setStream(mediaStream);
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch {
      showCameraPermissionMessage();
    }
  };

  const handleReplaceClick = () => {
    if (disabled || saving || !isEditing) return;
    if (cameraUpload) {
      startCamera();
      return;
    }
    fileInputRef.current?.click();
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    stopCamera();
    try {
      await commitUpload(imageData);
    } catch {
      // Parent handles errors
    }
  };

  const handleRemove = async (event) => {
    event?.stopPropagation?.();
    if (disabled || saving || !isEditing || !onRemove) return;
    await onRemove();
  };

  const textSecondary = isDark ? alpha('#fff', 0.62) : '#64748b';
  const frameSx = {
    position: 'relative',
    borderRadius: 2,
    overflow: 'hidden',
    border: `1px solid ${alpha(accent, isEditing ? 0.42 : 0.2)}`,
    bgcolor: isDark ? alpha('#ffffff', 0.03) : alpha(accent, 0.04),
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : '0 10px 24px rgba(15, 23, 42, 0.06)',
    minHeight: 136,
  };

  return (
    <>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: textSecondary,
            fontWeight: 700,
            mb: 0.85,
            display: 'block',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: '0.68rem',
          }}
        >
          {label}
        </Typography>

        {imageUrl ? (
          <Box>
            <Box
              sx={{
                ...frameSx,
                '&:hover .doc-preview-zoom': {
                  opacity: isEditing ? 0 : 1,
                },
              }}
            >
              <Box
                component="img"
                src={imageUrl}
                alt={label}
                onClick={() => {
                  if (!isEditing) window.open(imageUrl, '_blank', 'noopener,noreferrer');
                }}
                sx={{
                  width: '100%',
                  height: 136,
                  objectFit: 'cover',
                  display: 'block',
                  cursor: isEditing ? 'default' : 'pointer',
                  opacity: saving ? 0.65 : 1,
                }}
              />
              {!isEditing ? (
                <Box
                  className="doc-preview-zoom"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha('#020617', 0.28),
                    opacity: 0,
                    transition: 'opacity 0.2s ease',
                    pointerEvents: 'none',
                  }}
                >
                  <ZoomInIcon sx={{ color: '#fff', fontSize: 28 }} />
                </Box>
              ) : null}
              {saving ? (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha('#000', 0.28),
                  }}
                >
                  <CircularProgress size={24} sx={{ color: accent }} />
                </Box>
              ) : null}
            </Box>
            {isEditing ? (
              <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                  onClick={handleReplaceClick}
                  disabled={disabled || saving}
                  sx={{ textTransform: 'none', minWidth: 0 }}
                >
                  Replace
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                  onClick={handleRemove}
                  disabled={disabled || saving}
                  sx={{ textTransform: 'none', minWidth: 0 }}
                >
                  Remove
                </Button>
              </Stack>
            ) : null}
          </Box>
        ) : isEditing ? (
          <Paper
            elevation={0}
            onClick={handleReplaceClick}
            sx={{
              ...getGuestDrawerUploadPaperSx(isDark),
              ...frameSx,
              minHeight: 136,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: disabled || saving ? 'not-allowed' : 'pointer',
              opacity: disabled || saving ? 0.65 : 1,
            }}
          >
            <Box sx={{ textAlign: 'center', px: 1 }}>
              {saving ? (
                <CircularProgress size={24} sx={{ color: accent, mb: 1 }} />
              ) : cameraUpload ? (
                <CameraAltIcon sx={{ fontSize: 32, color: accent, mb: 0.75 }} />
              ) : (
                <CloudUploadIcon sx={{ fontSize: 32, color: accent, mb: 0.75 }} />
              )}
              <Typography variant="caption" sx={{ color: textSecondary, display: 'block' }}>
                {saving
                  ? 'Saving…'
                  : cameraUpload
                    ? 'Tap to capture'
                    : 'Tap to upload'}
              </Typography>
            </Box>
          </Paper>
        ) : (
          <Box
            sx={{
              ...frameSx,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              color: textSecondary,
            }}
          >
            <ImageNotSupportedOutlinedIcon sx={{ fontSize: 28, opacity: 0.55 }} />
            <Typography variant="caption" sx={{ color: textSecondary }}>
              Not uploaded
            </Typography>
          </Box>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileUpload}
        />
      </Box>

      <Dialog open={cameraOpen} onClose={stopCamera} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Capture {label}
          <IconButton size="small" onClick={stopCamera} aria-label="Close camera">
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box
            component="video"
            ref={videoRef}
            autoPlay
            playsInline
            muted
            sx={{ width: '100%', borderRadius: 1, bgcolor: '#000' }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={stopCamera}>Cancel</Button>
          <Button variant="contained" onClick={capturePhoto}>
            Capture
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={permissionSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setPermissionSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setPermissionSnackbar({ open: false, message: '' })}>
          {permissionSnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
